import { createElement, Component, useReducer, useMemo, createContext, useContext, useEffect } from 'react';
import { setDefaultOptions } from 'date-fns';
import { enUS, de } from 'date-fns/locale';
import { find } from 'lodash-es';
import { observable, computed, observe, reaction } from 'mobx';
import moment from 'moment';
import i18next from 'i18next';
import HttpApi from 'i18next-http-backend';
import { initReactI18next } from 'react-i18next';
export * from 'react-i18next';
import { jsx } from 'react/jsx-runtime';
import { inject } from 'mobx-react';

/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */
/* global Reflect, Promise, SuppressedError, Symbol, Iterator */

var extendStatics = function(d, b) {
    extendStatics = Object.setPrototypeOf ||
        ({ __proto__: [] } instanceof Array && function (d, b) { d.__proto__ = b; }) ||
        function (d, b) { for (var p in b) if (Object.prototype.hasOwnProperty.call(b, p)) d[p] = b[p]; };
    return extendStatics(d, b);
};

function __extends(d, b) {
    if (typeof b !== "function" && b !== null)
        throw new TypeError("Class extends value " + String(b) + " is not a constructor or null");
    extendStatics(d, b);
    function __() { this.constructor = d; }
    d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
}

var __assign = function() {
    __assign = Object.assign || function __assign(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};

function __decorate(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}

function __read(o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
}

typeof SuppressedError === "function" ? SuppressedError : function (error, suppressed, message) {
    var e = new Error(message);
    return e.name = "SuppressedError", e.error = error, e.suppressed = suppressed, e;
};

var I18NMock = function (_a) {
    var className = _a.className, term = _a.term;
    return createElement('span', { className: className }, term);
};
function i18nMock(component) {
    return function (props) {
        return createElement(component, __assign(__assign({}, props), { localisationStore: { translate: function (str) { return str; } }, currentLanguage: 'en' }));
    };
}

var localisationTestTermKey = 'test-term-key';
var localisationTestTermValue = 'test-term-value';
var I18nextMock = (function () {
    function I18nextMock() {
        this.t = function (term) { return (term === localisationTestTermKey ? localisationTestTermValue : term); };
        this.exists = function (_term) { return true; };
        this.changeLanguage = function (languageCode, cb) { return cb(null, languageCode); };
    }
    return I18nextMock;
}());
var i18nextMockInstance = new I18nextMock();

var MemoryStorage = require('memorystorage');
var selectedStorage;
try {
    var testStr = "fh-ship@test-localstorage".concat(Date.now());
    window.localStorage.setItem(testStr, testStr);
    var val = window.localStorage.getItem(testStr);
    window.localStorage.removeItem(testStr);
    if (testStr !== val) {
        throw new Error();
    }
    selectedStorage = window.localStorage;
}
catch (e) {
    selectedStorage = new MemoryStorage('fh-ship');
}
var storage = selectedStorage;

var defaultLng = storage.getItem('language') || 'en';
var initI18next = function (getLocalisationURL) {
    return new Promise(function (resolve, _reject) {
        i18next
            .use(initReactI18next)
            .use(HttpApi)
            .init({
            interpolation: {
                format: function (value, format, _lng) {
                    if (format === 'compactCount') {
                        return Number(value) > 9 ? '9+' : value;
                    }
                    return value;
                },
            },
            backend: {
                allowMultiLoading: false,
                crossDomain: true,
                loadPath: function (languageCode) {
                    return getLocalisationURL("/localisation/".concat(languageCode[0]));
                },
            },
            debug: false,
            fallbackLng: 'en',
            lng: defaultLng,
            returnEmptyString: false,
        }, function (err, _t) {
            if (err) {
                console.error(err);
            }
            else {
                resolve(i18next);
            }
        });
    });
};

function buildFormatLongFn(args) {
    return function (options) {
        if (options === void 0) { options = {}; }
        var width = options.width ? options.width : args.defaultWidth;
        var format = args.formats[width] || args.formats[args.defaultWidth];
        return format;
    };
}
var dateFormats = {
    full: "EEEE, MMMM do, y",
    long: "MMMM do, y",
    medium: "MMM d, y",
    short: "yyyy-MM-dd",
};
var timeFormats = {
    full: "HH:mm:ss (zzzz)",
    long: "HH:mm:ss (z)",
    medium: "HH:mm:ss",
    short: "HH:mm",
};
var dateTimeFormats = {
    full: "{{date}} 'at' {{time}}",
    long: "{{date}} 'at' {{time}}",
    medium: "{{date}} {{time}}",
    short: "{{date}} {{time}}",
};
var formatLong = {
    date: buildFormatLongFn({
        formats: dateFormats,
        defaultWidth: "full",
    }),
    time: buildFormatLongFn({
        formats: timeFormats,
        defaultWidth: "full",
    }),
    dateTime: buildFormatLongFn({
        formats: dateTimeFormats,
        defaultWidth: "full",
    }),
};
var isoLocale = __assign(__assign({}, enUS), { code: 'iso', formatLong: formatLong, options: {
        weekStartsOn: 1,
        firstWeekContainsDate: 4,
    } });

var availableLanguages = [
    { code: 'en', name: 'English' },
    { code: 'de', name: 'Deutsch' },
];
var localeData = moment.localeData('en');
moment.updateLocale('en', {
    parentLocale: 'en',
    longDateFormat: {
        LTS: localeData.longDateFormat('LTS'),
        LT: localeData.longDateFormat('LT'),
        L: 'YYYY-MM-DD',
        LL: localeData.longDateFormat('LL'),
        LLL: 'YYYY-MM-DD HH:mm',
        LLLL: 'YYYY-MM-DD HH:mm (z)',
    },
    week: {
        dow: 1,
    },
});
var locales = {
    en: isoLocale,
    de: de,
};
var LocalisationStore = (function () {
    function LocalisationStore(props) {
        var _this = this;
        this.availableLanguages = availableLanguages;
        this.isInitialized = false;
        this.setClient = function (client) {
            _this.client = client;
            _this.isInitialized = true;
        };
        this.changeLanguage = function (languageCode) {
            _this.eventBus.dispatchEvent('language-changed', {
                languageCode: languageCode,
            });
            return new Promise(function (resolve, reject) {
                _this.client.changeLanguage(languageCode, function (err, t) {
                    if (err) {
                        return reject(err);
                    }
                    return resolve(t);
                });
            });
        };
        this.setLanguage = function (languageCode) {
            if (_this.current === languageCode) {
                return Promise.resolve({});
            }
            languageCode = languageCode || _this.default;
            storage.setItem('language', languageCode);
            _this.setLibrariesLocale(languageCode);
            return _this.changeLanguage(languageCode)
                .then(function () {
                _this.current = languageCode;
            })
                .catch(function (err) { return console.error('[Error infra]', err); });
        };
        this.translate = function (term, options) {
            return _this.client.t(term, __assign(__assign({}, options), { nsSeparator: '^&%' })) || term;
        };
        this.exists = function (term) {
            return _this.client.exists(term);
        };
        this.translateIfExists = function (term) {
            return _this.exists(term) ? _this.translate(term) : term;
        };
        this.default = defaultLng;
        this.current = defaultLng;
        storage.setItem('language', this.current);
        this.setLibrariesLocale(this.current);
        if (props.client) {
            this.setClient(props.client);
        }
        else if (props.getClient) {
            props.getClient().then(this.setClient);
        }
        this.eventBus = props.eventBus;
    }
    LocalisationStore.prototype.setLibrariesLocale = function (languageCode) {
        moment.locale(languageCode);
        var locale = locales[languageCode];
        setDefaultOptions({ locale: locale });
    };
    Object.defineProperty(LocalisationStore.prototype, "currentName", {
        get: function () {
            var _this = this;
            return find(this.availableLanguages, function (lang) { return lang.code === _this.current; }).name;
        },
        enumerable: false,
        configurable: true
    });
    __decorate([
        observable
    ], LocalisationStore.prototype, "current", void 0);
    __decorate([
        observable
    ], LocalisationStore.prototype, "isInitialized", void 0);
    __decorate([
        computed
    ], LocalisationStore.prototype, "currentName", null);
    return LocalisationStore;
}());

var localisationStoreMockInstance = new LocalisationStore({
    client: i18nextMockInstance,
    eventBus: { dispatchEvent: function () { return undefined; } },
});

var EventBus = (function () {
    function EventBus() {
        this.bus = document.createElement('busElement');
    }
    EventBus.prototype.addEventListener = function (event, callback) {
        this.bus.addEventListener(event, callback);
    };
    EventBus.prototype.removeEventListener = function (event, callback) {
        this.bus.removeEventListener(event, callback);
    };
    EventBus.prototype.dispatchEvent = function (event, detail) {
        if (detail === void 0) { detail = {}; }
        this.bus.dispatchEvent(new CustomEvent(event, { detail: detail }));
    };
    return EventBus;
}());

var PureI18N = (function (_super) {
    __extends(PureI18N, _super);
    function PureI18N() {
        return _super !== null && _super.apply(this, arguments) || this;
    }
    PureI18N.prototype.render = function () {
        var _a = this.props, count = _a.count, className = _a.className, params = _a.params, localisationStore = _a.localisationStore, term = _a.term;
        var options = params || {};
        if (count)
            options.count = count;
        var translated = localisationStore.translate(term, options) || '';
        return jsx("span", { className: className, children: translated });
    };
    return PureI18N;
}(Component));
function pureI18n(component) {
    return (function (_super) {
        __extends(I18nComponent, _super);
        function I18nComponent() {
            return _super !== null && _super.apply(this, arguments) || this;
        }
        I18nComponent.prototype.UNSAFE_componentWillMount = function () {
            var _this = this;
            if (component.UNSAFE_componentWillMount)
                component.UNSAFE_componentWillMount();
            this.dispose = observe(this.props.localisationStore, 'current', function () {
                _this.forceUpdate();
            });
        };
        I18nComponent.prototype.componentWillUnmount = function () {
            if (component.componentWillUnmount)
                component.componentWillUnmount();
            this.dispose();
        };
        I18nComponent.prototype.render = function () {
            var localisationStore = this.props.localisationStore;
            return createElement(component, __assign(__assign({}, this.props), { localisationStore: localisationStore, currentLanguage: localisationStore.current }));
        };
        return I18nComponent;
    }(Component));
}
function i18n(component) {
    return inject(function (ctx) { return ({
        localisationStore: ctx.localisationStore,
    }); })(pureI18n(component));
}
var I18N = i18n(PureI18N);

function useForceUpdate() {
    var _a = __read(useReducer(function (state, _action) { return !state; }, true), 2), forceUpdateFlag = _a[0], dispatch = _a[1];
    var forceUpdate = useMemo(function () { return function () { return dispatch(undefined); }; }, [dispatch]);
    return { forceUpdate: forceUpdate, forceUpdateFlag: forceUpdateFlag };
}

var LocalisationContext = createContext(undefined);
function useI18n() {
    var localisationStore = useContext(LocalisationContext);
    var forceUpdate = useForceUpdate().forceUpdate;
    if (!localisationStore) {
        throw new Error('Must use useI18n within a LocalisationContext');
    }
    useEffect(function () { return reaction(function () { return localisationStore.current; }, forceUpdate); }, []);
    return localisationStore;
}

export { EventBus, I18N, I18NMock, LocalisationContext, LocalisationStore, PureI18N, availableLanguages, i18n, i18nMock, i18nextMockInstance, initI18next, isoLocale, localisationStoreMockInstance, pureI18n, useI18n };
//# sourceMappingURL=index.es.js.map
